/**
 * Cloud Sync Settings Dialog
 *
 * UI component for managing cloud synchronization settings
 */

import React, { useState, useEffect } from "react";
import {
    cloudStorage,
    generateSyncKey,
    type CloudSyncConfig,
    type DrawingMetadata,
} from "../data/CloudStorage";
import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import "./SyncSettings.scss";

interface SyncSettingsProps {
    onClose: () => void;
    onSyncComplete?: () => void;
    excalidrawAPI: ExcalidrawImperativeAPI | null;
}

type ViewMode = "initial" | "setup" | "active" | "browse";
type SetupMode = "new" | "existing";

export const SyncSettings: React.FC<SyncSettingsProps> = ({
    onClose,
    onSyncComplete,
    excalidrawAPI,
}) => {
    const [viewMode, setViewMode] = useState<ViewMode>("initial");
    const [setupMode, setSetupMode] = useState<SetupMode>("new");
    const [syncKey, setSyncKey] = useState("");
    const [generatedKey, setGeneratedKey] = useState("");
    const [autoSync, setAutoSync] = useState(true);
    const [autoLoad, setAutoLoad] = useState(true);
    const [config, setConfig] = useState<CloudSyncConfig | null>(null);
    const [drawings, setDrawings] = useState<DrawingMetadata[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    useEffect(() => {
        // Check if already configured
        const existingConfig = cloudStorage.getConfig();
        if (existingConfig) {
            setConfig(existingConfig);
            setViewMode("active");
            loadDrawings();
        }
    }, []);

    const loadDrawings = async () => {
        try {
            setLoading(true);
            const list = await cloudStorage.listDrawings();
            setDrawings(list);
        } catch (err: any) {
            setError(err.message || "Failed to load drawings");
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateKey = () => {
        const key = generateSyncKey();
        setGeneratedKey(key);
        setSyncKey(key);
    };

    const handleSetupSync = async () => {
        if (!syncKey.trim()) {
            setError("Please enter or generate a sync key");
            return;
        }

        try {
            setLoading(true);
            setError("");

            await cloudStorage.initialize(syncKey, autoSync, autoLoad);
            setConfig(cloudStorage.getConfig());
            setSuccess("云同步配置成功！");

            setTimeout(() => {
                setViewMode("active");
                setSuccess("");
                loadDrawings();
            }, 1500);
        } catch (err: any) {
            setError(err.message || "同步配置失败");
        } finally {
            setLoading(false);
        }
    };


    const handleSyncNow = async () => {
        console.log("[SyncSettings] handleSyncNow called");

        if (!excalidrawAPI) {
            console.error("[SyncSettings] excalidrawAPI is null");
            setError("Excalidraw API 不可用");
            return;
        }

        try {
            setLoading(true);
            setError("");
            console.log("[SyncSettings] Starting manual sync...");

            // Get current canvas data
            const elements = excalidrawAPI.getSceneElements();
            const appState = excalidrawAPI.getAppState();
            const files = excalidrawAPI.getFiles();

            console.log("[SyncSettings] Canvas data:", {
                elementCount: elements.length,
                fileCount: Object.keys(files).length,
                drawingName: appState.name || "未命名"
            });

            if (elements.length === 0) {
                console.warn("[SyncSettings] Canvas is empty");
                setError("画布为空。请先添加一些内容再同步。");
                setLoading(false);
                return;
            }

            // Get drawing name from appState or use default
            const drawingName = appState.name || "未命名";

            // Get or create drawing ID
            let drawingId = localStorage.getItem("excalidraw-current-drawing-id");
            if (!drawingId) {
                drawingId = `drawing-${Date.now()}-${Math.random()
                    .toString(36)
                    .substr(2, 9)}`;
                localStorage.setItem("excalidraw-current-drawing-id", drawingId);
                console.log("[SyncSettings] Created new drawing ID:", drawingId);
            } else {
                console.log("[SyncSettings] Using existing drawing ID:", drawingId);
            }

            console.log("[SyncSettings] Uploading to cloud...");
            // Upload to cloud
            await cloudStorage.uploadDrawing(drawingId, drawingName, {
                elements,
                appState,
                files,
            });

            console.log("[SyncSettings] Upload successful!");
            setSuccess("同步成功！");
            await loadDrawings(); // Refresh the list

            if (onSyncComplete) {
                onSyncComplete();
            }

            setTimeout(() => setSuccess(""), 2000);
        } catch (err: any) {
            console.error("[SyncSettings] Sync error:", err);
            setError(err.message || "同步失败");
        } finally {
            setLoading(false);
        }
    };

    const handleLoadDrawing = async (drawingId: string) => {
        if (!excalidrawAPI) {
            setError("Excalidraw API 不可用");
            return;
        }

        try {
            setLoading(true);
            setError("");

            const data = await cloudStorage.downloadDrawing(drawingId);

            // Update the current drawing ID
            localStorage.setItem("excalidraw-current-drawing-id", drawingId);

            // Load the data into excalidraw
            excalidrawAPI.updateScene({
                elements: data.elements,
            });

            // Update the drawing name separately if needed
            if (data.name) {
                excalidrawAPI.updateScene({
                    appState: { name: data.name },
                });
            }

            // Load files if any
            if (data.files && Object.keys(data.files).length > 0) {
                excalidrawAPI.addFiles(
                    Object.values(data.files).map((file: any) => ({
                        ...file,
                        created: file.created || Date.now(),
                        lastRetrieved: Date.now(),
                    })),
                );
            }

            setSuccess(`已加载 "${data.name}"`);

            if (onSyncComplete) {
                onSyncComplete();
            }

            setTimeout(() => {
                onClose();
            }, 1000);
        } catch (err: any) {
            console.error("Load error:", err);
            setError(err.message || "加载绘图失败");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteDrawing = async (drawingId: string) => {
        if (
            !confirm("您确定要从云端删除此绘图吗？")
        ) {
            return;
        }

        try {
            setLoading(true);
            setError("");

            await cloudStorage.deleteDrawing(drawingId);
            setSuccess("绘图已删除");
            await loadDrawings();

            setTimeout(() => setSuccess(""), 2000);
        } catch (err: any) {
            setError(err.message || "删除绘图失败");
        } finally {
            setLoading(false);
        }
    };

    const handleDisableSync = () => {
        if (
            !confirm(
                "您确定要禁用云同步吗？您的同步密钥将被移除。",
            )
        ) {
            return;
        }

        cloudStorage.clearConfig();
        setConfig(null);
        setViewMode("initial");
        setSyncKey("");
        setGeneratedKey("");
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        setSuccess("已复制到剪贴板！");
        setTimeout(() => setSuccess(""), 2000);
    };

    const renderInitialView = () => (
        <div className="sync-settings-initial">
            <h2>☁️ 云同步</h2>
            <p className="description">
                启用云同步，在多个设备上访问您的绘图。所有数据都经过端到端加密，确保您的隐私安全。
            </p>

            <div className="action-buttons">
                <button
                    className="primary-button"
                    onClick={() => {
                        setSetupMode("new");
                        setViewMode("setup");
                        handleGenerateKey();
                    }}
                >
                    🔑 生成新的同步密钥
                </button>

                <button
                    className="secondary-button"
                    onClick={() => {
                        setSetupMode("existing");
                        setViewMode("setup");
                    }}
                >
                    📥 使用已存在的同步密钥
                </button>
            </div>

            <div className="info-box">
                <strong>ℹ️ 如何工作:</strong>
                <ul>
                    <li>生成一个唯一的同步密钥或使用已存在的密钥</li>
                    <li>您的绘图在上传前会被加密</li>
                    <li>在其他设备上使用相同的密钥来同步</li>
                    <li>请妥善保管您的同步密钥 - 如果丢失，将无法恢复</li>
                </ul>
            </div>
        </div>
    );

    const renderSetupView = () => (
        <div className="sync-settings-setup">
            <button className="back-button" onClick={() => setViewMode("initial")}>
                ← 返回
            </button>

            <h2>{setupMode === "new" ? "🔑 新同步密钥" : "📥 输入同步密钥"}</h2>

            {setupMode === "new" && generatedKey && (
                <div className="generated-key-box">
                    <p className="label">您的同步密钥：</p>
                    <div className="key-display">
                        <code>{generatedKey}</code>
                        <button
                            className="copy-button"
                            onClick={() => copyToClipboard(generatedKey)}
                            title="复制到剪贴板"
                        >
                            📋
                        </button>
                    </div>
                    <div className="warning-box">
                        <strong>⚠️ 重要提示：</strong>
                        <ul>
                            <li>请将此密钥保存在安全的地方</li>
                            <li>您需要此密钥在其他设备上同步</li>
                            <li>如果丢失，将无法恢复</li>
                        </ul>
                    </div>
                </div>
            )}

            {setupMode === "existing" && (
                <div className="sync-key-input">
                    <label htmlFor="sync-key">请输入您的同步密钥：</label>
                    <input
                        id="sync-key"
                        type="text"
                        value={syncKey}
                        onChange={(e) => setSyncKey(e.target.value)}
                        placeholder="abc-def-ghi-jkl-mno"
                        className="key-input"
                    />
                </div>
            )}

            <div className="sync-options">
                <label className="checkbox-label">
                    <input
                        type="checkbox"
                        checked={autoSync}
                        onChange={(e) => setAutoSync(e.target.checked)}
                    />
                    <span>启用自动同步</span>
                </label>
                <label className="checkbox-label">
                    <input
                        type="checkbox"
                        checked={autoLoad}
                        onChange={(e) => setAutoLoad(e.target.checked)}
                    />
                    <span>启用自动加载（应用启动时）</span>
                </label>
            </div>

            {error && <div className="error-message">❌ {error}</div>}
            {success && <div className="success-message">✅ {success}</div>}

            <button
                className="primary-button"
                onClick={handleSetupSync}
                disabled={loading || !syncKey}
            >
                {loading ? "正在设置..." : "启用云同步"}
            </button>
        </div>
    );

    const renderActiveView = () => (
        <div className="sync-settings-active">
            <h2>☁️ 云同步已激活</h2>

            <div className="sync-status">
                <div className="status-item">
                    <span className="label">状态：</span>
                    <span className="value success">✅ 已连接</span>
                </div>
                <div className="status-item">
                    <span className="label">自动同步：</span>
                    <span className="value">
                        {config?.autoSync ? "✅ 已启用" : "❌ 已禁用"}
                    </span>
                </div>
                <div className="status-item">
                    <span className="label">自动加载：</span>
                    <span className="value">
                        {config?.autoLoad ? "✅ 已启用" : "❌ 已禁用"}
                    </span>
                </div>
                <div className="status-item">
                    <span className="label">同步密钥：</span>
                    <div className="key-display-small">
                        <code>
                            {config?.syncKey.substring(0, 16)}...{config?.syncKey.slice(-8)}
                        </code>
                        <button
                            className="copy-button-small"
                            onClick={() => copyToClipboard(config?.syncKey || "")}
                            title="复制完整密钥"
                        >
                            📋
                        </button>
                    </div>
                </div>
                {config?.lastSyncTime && (
                    <div className="status-item">
                        <span className="label">上次同步：</span>
                        <span className="value">
                            {new Date(config.lastSyncTime).toLocaleString()}
                        </span>
                    </div>
                )}
            </div>

            <div className="action-buttons">
                <button
                    className="primary-button"
                    onClick={handleSyncNow}
                    disabled={loading}
                >
                    {loading ? "同步中..." : "🔄 立即同步"}
                </button>

                <button
                    className="secondary-button"
                    onClick={() => setViewMode("browse")}
                >
                    📁 浏览云端绘图
                </button>

                <button className="danger-button" onClick={handleDisableSync}>
                    🚫 禁用云同步
                </button>
            </div>

            {error && <div className="error-message">❌ {error}</div>}
            {success && <div className="success-message">✅ {success}</div>}
        </div>
    );

    const renderBrowseView = () => (
        <div className="sync-settings-browse">
            <button className="back-button" onClick={() => setViewMode("active")}>
                ← 返回
            </button>

            <h2>📁 云端绘图</h2>

            {loading && <div className="loading">加载中...</div>}

            {!loading && drawings.length === 0 && (
                <div className="empty-state">
                    <p>云端存储中未找到绘图。</p>
                    <p>创建并同步一个绘图后，它将显示在这里。</p>
                </div>
            )}

            {!loading && drawings.length > 0 && (
                <div className="drawings-list">
                    {drawings.map((drawing) => (
                        <div key={drawing.id} className="drawing-item">
                            <div className="drawing-info">
                                <div className="drawing-name">{drawing.name}</div>
                                <div className="drawing-meta">
                                    {new Date(drawing.updatedAt).toLocaleString()} •{" "}
                                    {(drawing.size / 1024).toFixed(2)} KB
                                </div>
                            </div>
                            <div className="drawing-actions">
                                <button
                                    className="load-button"
                                    onClick={() => handleLoadDrawing(drawing.id)}
                                    title="加载此绘图"
                                >
                                    📥 加载
                                </button>
                                <button
                                    className="delete-button"
                                    onClick={() => handleDeleteDrawing(drawing.id)}
                                    title="从云端删除"
                                >
                                    🗑️
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {error && <div className="error-message">❌ {error}</div>}
            {success && <div className="success-message">✅ {success}</div>}
        </div>
    );

    return (
        <Dialog onCloseRequest={onClose} size="wide" title={false}>
            <div className="sync-settings-dialog">
                {viewMode === "initial" && renderInitialView()}
                {viewMode === "setup" && renderSetupView()}
                {viewMode === "active" && renderActiveView()}
                {viewMode === "browse" && renderBrowseView()}
            </div>
        </Dialog>
    );
};
